import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@truefit/http-utils';
import { useSnackbar } from 'notistack';
import {
  S3_DESTINATIONS_QUERY_KEY,
  type DispatchS3Destination,
  type S3DestinationAuthMode,
} from './useDispatchS3DestinationsQuery';

export interface CreateS3DestinationData {
  label?: string;
  bucket: string;
  baseDir?: string;
  region: string;
  departmentId?: number;
  authMode: S3DestinationAuthMode;
  // static-only
  accessKey?: string;
  secretKey?: string;
  // assume-role-only
  roleArn?: string;
  externalId?: string;
}

export const useCreateDispatchS3DestinationMutation = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: CreateS3DestinationData) => {
      const res = await post<DispatchS3Destination>('/v1.0/clip-dispatch/s3-destinations', data);
      return res.data;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: S3_DESTINATIONS_QUERY_KEY });

      // Surface the plaintext externalId once so the user can paste it
      // into their AWS trust policy. The next GET will return it masked.
      if (created.authMode === 'assume-role' && created.externalId) {
        enqueueSnackbar(
          `S3 destination added. External ID: ${created.externalId} — copy this into your AWS role's trust policy.`,
          { variant: 'success', persist: true },
        );
      } else {
        enqueueSnackbar('S3 destination added', { variant: 'success' });
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.error || 'Failed to add S3 destination', { variant: 'error' });
    },
  });
};
